import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HiSparkles, HiXMark, HiPaperAirplane, HiAcademicCap, HiTrash, HiClipboardDocument } from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import { sendAIMessage, parseSSEStream } from '../../services/aiService';
import './AIAssistant.css';

const QUICK_PROMPTS = [
  '📋 Summarize recent posts',
  '🔬 What\'s trending on campus?',
  '👥 Who should I connect with?',
  '📊 My activity summary',
  '💡 Research writing tips',
  '🎓 Career advice for academics',
];

// Simple markdown-like rendering
function renderAIText(text) {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^- /gm, '• ')
    .replace(/^(\d+)\. /gm, '$1. ');
}

function AIAssistant() {
  const { user, profile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      text: `Hi${profile?.name ? ` ${profile.name.split(' ')[0]}` : ''}! 👋 I'm your **Campus AI** — I know what's happening on the platform. Ask me about recent posts, trending topics, your connections, or anything academic!`,
      time: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [copiedId, setCopiedId] = useState(null);

  // Draggable FAB state
  const fabRef = useRef(null);
  const [fabPos, setFabPos] = useState(() => {
    const saved = localStorage.getItem('ai_fab_pos');
    return saved ? JSON.parse(saved) : null;
  });
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0, moved: false });

  const handleDragStart = useCallback((e) => {
    if (isOpen) return;
    const touch = e.touches?.[0] || e;
    const fab = fabRef.current;
    if (!fab) return;
    const rect = fab.getBoundingClientRect();
    dragState.current = {
      dragging: true,
      startX: touch.clientX,
      startY: touch.clientY,
      startLeft: rect.left,
      startTop: rect.top,
      moved: false,
    };
  }, [isOpen]);

  useEffect(() => {
    const handleMove = (e) => {
      if (!dragState.current.dragging) return;
      const touch = e.touches?.[0] || e;
      const dx = touch.clientX - dragState.current.startX;
      const dy = touch.clientY - dragState.current.startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) dragState.current.moved = true;
      if (!dragState.current.moved) return;
      e.preventDefault();
      const newLeft = Math.max(0, Math.min(window.innerWidth - 60, dragState.current.startLeft + dx));
      const newTop = Math.max(0, Math.min(window.innerHeight - 60, dragState.current.startTop + dy));
      setFabPos({ left: newLeft, top: newTop });
    };
    const handleEnd = () => {
      if (dragState.current.moved && fabPos) {
        localStorage.setItem('ai_fab_pos', JSON.stringify(fabPos));
      }
      dragState.current.dragging = false;
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [fabPos]);

  // Keyboard shortcut: Ctrl+/ to toggle
  useEffect(() => {
    const handleKeyboard = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSendMessage = async (text) => {
    if (!text.trim() || isTyping) return;

    const userMsg = {
      id: Date.now(),
      role: 'user',
      text: text.trim(),
      time: new Date(),
    };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setIsTyping(true);

    try {
      const apiMessages = updatedMessages
        .filter(m => m.id !== 'welcome')
        .slice(-12)
        .map(m => ({
          role: m.role === 'assistant' ? 'assistant' : 'user',
          content: m.text,
        }));

      const result = await sendAIMessage(apiMessages, user?.id);

      if (result.stream && result.body) {
        // Streaming response - show text as it arrives
        const streamMsgId = Date.now() + 1;
        setMessages(prev => [...prev, {
          id: streamMsgId,
          role: 'assistant',
          text: '',
          time: new Date(),
          streaming: true,
        }]);
        setIsTyping(false);

        let fullText = '';
        const updateMessage = (text) => {
          setMessages(prev => prev.map(m =>
            m.id === streamMsgId ? { ...m, text } : m
          ));
        };
        for await (const chunk of parseSSEStream(result.body)) {
          fullText += chunk;
          updateMessage(fullText);
        }
        // Mark streaming complete
        setMessages(prev => prev.map(m =>
          m.id === streamMsgId ? { ...m, streaming: false } : m
        ));
      } else {
        // Non-streaming response
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          role: 'assistant',
          text: result.reply,
          time: new Date(),
        }]);
        setIsTyping(false);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        text: "I'm having trouble connecting right now. Please try again in a moment! 🔄",
        time: new Date(),
      }]);
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(input);
    }
  };

  const handleQuickPrompt = (prompt) => {
    const cleanPrompt = prompt.replace(/^[^\w]+/, '').trim();
    handleSendMessage(cleanPrompt);
  };

  const handleFabClick = () => {
    if (dragState.current.moved) {
      dragState.current.moved = false;
      return;
    }
    setIsOpen(prev => !prev);
  };

  const clearChat = useCallback(() => {
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      text: `Hi${profile?.name ? ` ${profile.name.split(' ')[0]}` : ''}! 👋 I'm your **Campus AI** — I know what's happening on the platform. Ask me anything!`,
      time: new Date(),
    }]);
  }, [profile]);

  const copyMessage = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const fabStyle = fabPos ? {
    left: fabPos.left,
    top: fabPos.top,
    right: 'auto',
    bottom: 'auto',
  } : {};

  return (
    <>
      {/* Floating Toggle Button - Draggable */}
      <button
        ref={fabRef}
        className={`ai-fab ${isOpen ? 'ai-fab--open' : ''}`}
        onClick={handleFabClick}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        style={fabStyle}
        aria-label={isOpen ? 'Close AI Assistant' : 'Open AI Assistant'}
        title="AI Assistant (Ctrl+/) — Drag to reposition"
      >
        <span className="ai-fab-icon">
          {isOpen ? <HiXMark /> : <HiSparkles />}
        </span>
        {!isOpen && <span className="ai-fab-pulse" />}
      </button>

      {/* Chat Panel */}
      <div className={`ai-panel ${isOpen ? 'ai-panel--open' : ''}`}>
        {/* Header */}
        <div className="ai-panel-header">
          <div className="ai-panel-header-info">
            <div className="ai-avatar">
              <HiAcademicCap />
            </div>
            <div>
              <h3>Campus AI</h3>
              <span className="ai-status">
                <span className="ai-status-dot" />
                Powered by AI · Live Data
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button className="ai-panel-close" onClick={clearChat} aria-label="Clear chat" title="Clear chat">
              <HiTrash />
            </button>
            <button className="ai-panel-close" onClick={() => setIsOpen(false)} aria-label="Close">
              <HiXMark />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="ai-panel-messages">
          {messages.map((msg) => (
            <div key={msg.id} className={`ai-msg ai-msg--${msg.role}`}>
              {msg.role === 'assistant' && (
                <div className="ai-msg-avatar">
                  <HiAcademicCap />
                </div>
              )}
              <div className="ai-msg-bubble">
                <p
                  className="ai-msg-text"
                  dangerouslySetInnerHTML={{ __html: renderAIText(msg.text) }}
                />
                {msg.streaming && <span className="ai-streaming-cursor">▊</span>}
                <div className="ai-msg-footer">
                  <span className="ai-msg-time">{formatTime(msg.time)}</span>
                  {msg.role === 'assistant' && msg.id !== 'welcome' && !msg.streaming && (
                    <button
                      className="ai-msg-copy"
                      onClick={() => copyMessage(msg.id, msg.text)}
                      title="Copy response"
                    >
                      {copiedId === msg.id ? '✓' : <HiClipboardDocument />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="ai-msg ai-msg--assistant">
              <div className="ai-msg-avatar">
                <HiAcademicCap />
              </div>
              <div className="ai-msg-bubble ai-typing">
                <span className="ai-typing-dot" />
                <span className="ai-typing-dot" />
                <span className="ai-typing-dot" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Prompts */}
        {messages.length <= 1 && (
          <div className="ai-quick-prompts">
            {QUICK_PROMPTS.map((prompt, i) => (
              <button
                key={i}
                className="ai-quick-prompt"
                onClick={() => handleQuickPrompt(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="ai-panel-input">
          <input
            ref={inputRef}
            type="text"
            placeholder="Ask about posts, people, trends..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isTyping}
          />
          <button
            className="ai-send-btn"
            onClick={() => handleSendMessage(input)}
            disabled={!input.trim() || isTyping}
            aria-label="Send message"
          >
            <HiPaperAirplane />
          </button>
        </div>
      </div>

      {/* Backdrop on mobile */}
      {isOpen && <div className="ai-backdrop" onClick={() => setIsOpen(false)} />}
    </>
  );
}

export default AIAssistant;
